<template>
  <vue-resizable :min-height="50" :height="Number.parseInt(theHeight)" :active="handlers" @resize:end="resized">
    <leaflet-map
      ref="fullMap"
      style="height: 100%;"
      fit-once
      :geolocate="false"
      @marker-over="markerOverOut($event, [35.1, 46.8], [17.55, 48.8])"
      @marker-out="markerOverOut($event, [27, 36], [18, 39])"
      @delete-from-tour="preDeleteFromTour"
      :without-marker-cluster="!withoutMarkerCluster"
    >
      <template #controls-topright>
        <div class="mt-2 leaflet-control-layers leaflet-control-layers-expanded">
          <div v-if="withTracking && tours.length === 1 && tours[0] && tours[0].courier_id">
            <b-form-checkbox v-model="showTracking">
              <b-spinner small v-if="trackingWorking"></b-spinner>
              <translate>Show tracking</translate>
            </b-form-checkbox>
            <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
              <translate>Show heathPath</translate>
            </b-form-checkbox>
          </div>
          <b-form-checkbox v-model="withoutMarkerCluster" :value="true" :unchecked-value="false">
            <translate>Group Near Pins</translate>
          </b-form-checkbox>
        </div>
      </template>
    </leaflet-map>
  </vue-resizable>
</template>

<script>
import {
  TrackingService,
  TourService
} from '@/services/api'
import ToursMixin from '@/mixins/tours.mixin'
import VueResizable from 'vue-resizable'
import LeafletMap from '@/components/Maps/LeafletMap'

export default {
  emits: ['delete-from-tour'],
  name: 'TourMap',
  components: {
    VueResizable,
    LeafletMap
  },
  props: {
    tourIds: {
      type: Array,
      default: () => []
    },
    withTracking: {
      type: Boolean,
      default: false
    },
    popupType: {
      type: String,
      default: 'shipping'
    },
    height: {
      type: [String, Number],
      default: 500
    },
    handlers: {
      type: Array,
      default: () => ['r', 'rb', 'b', 'lb', 'l', 'lt', 't', 'rt']
    },
    withCourier: {
      type: Boolean,
      default: false
    },
    updateEvery: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      tours: [],
      markers: [],
      polylines: [],
      routeColors: [],
      lastId: null,
      mapDataIndex: 0,
      theHeight: 0,
      tracking: [],
      heathPath: [],
      showTracking: false,
      showHeathPath: false,
      trackingWorking: false,
      withoutMarkerCluster: false
    }
  },
  watch: {
    tours(val) {
      setTimeout(function () {
        this.openTourMap()
      }.bind(this, val), 500)
    },
    markers(val) {
      setTimeout(function () {
        this.$refs.fullMap.addMarkers(val)
        this.$refs.fullMap.addPolylines(this.polylines)
      }.bind(this, val), 500)
    },
    tracking(val) {
      setTimeout(function () {
        this.$refs.fullMap.addTracking(val)
      }.bind(this, val), 500)
      if (val.length === 0) {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    heathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    showTracking(val) {
      if (val) {
        this.trackingWorking = true
        const params = {
          page: 0,
          limit: 1,
          date_from: this.tours[0].started_at ? this.moment.utc(this.tours[0].started_at).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss') : this.moment.utc(this.tours[0].updated_at).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
          date_to: this.tours[0].started_at ? this.moment.utc(this.tours[0].started_at).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss') : this.moment.utc(this.tours[0].updated_at).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
          filter_groups: [
            {
              filters: [
                ['user_id', 'eq', this.tours[0].courier_id]
              ]
            }
          ]
        }

        TrackingService.getAll({ params })
          .then(response => {
            const markers = []

            const uniqueTrackingPointsByGeo = response.body.data.filter((thing, index, self) =>
              index === self.findIndex((t) => {
                return thing.lat === t.lat && thing.lng === t.lng
              })
            )
            this.trackingWorking = false

            let timeThreshold = null
            uniqueTrackingPointsByGeo.forEach((item, i) => {
              if (timeThreshold === null || Number.parseInt(item.created_at.replaceAll(/\D/g, '')) > timeThreshold) {
                markers.push({
                  id: 1000 + i,
                  lat: item.lat,
                  lng: item.lng,
                  icon: {
                    type: 'marker-circle',
                    size: [10, 10],
                    anchor: [5, 5],
                    color: '#000',
                    text: (i + 1)
                  },
                  x: 11,
                  y: 34,
                  infoText: (i + 1) + ': ' + this.moment.utc(item.created_at).tz(this.$tz).format('L LT'),
                  tooltip: true,
                  z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
                })
                timeThreshold = Number.parseInt(item.created_at.replaceAll(/\D/g, '')) + 10
              }
            })
            this.tracking = markers
          })
      } else {
        this.tracking = []
        this.showHeathPath = false
      }
    },
    showHeathPath(val) {
      if (val) {
        this.heathPath = this.tracking.map(x => {
          return [x.lat, x.lng, x.z]
        })
      } else {
        this.heathPath = []
      }
    }
  },
  mixins: [
    ToursMixin
  ],
  mounted() {
    this.theHeight = this.$cookies.get('tour-map-height') || this.height
    this.fetchTours()
  },
  beforeDestroy() {
    clearTimeout(this.fetchTimeout)
  },
  methods: {
    fetchTours() {
      if (this.updateEvery) {
        clearTimeout(this.fetchTimeout)
      }
      const params = {
        includes: [
          'visits',
          'courier.courier_available.tracking',
          'shippings'
        ],
        filter_groups: [
          {
            filters: [
              ['id', 'in', this.tourIds]
            ]
          }
        ]
      }
      TourService.getAll({ params })
        .then(response => {
          this.tours = response.body.data.map(x => {
            return this.realVisits(x)
          })
          this.openTourMap()
          if (this.updateEvery) {
            this.fetchTimeout = setTimeout(() => {
              this.fetchTours()
            }, this.updateEvery * 1000)
          }
        })
    },
    prepareTourMapData(row, index, withCourier) {
      let lat
      let lng
      let icon = ''
      const markers = []
      let data = {}

      if (row.courier && row.courier.courier_available && row.courier.courier_available.tracking) {
        if (row.courier.courier_available.tracking[0] && withCourier) {
          icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === row.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'

          markers.push({
            id: 1000,
            lat: row.courier.courier_available.tracking[0].lat,
            lng: row.courier.courier_available.tracking[0].lng,
            icon,
            x: 25,
            y: 25,
            infoText: { id: row.courier.id, type: 'courier', user: row.courier },
            popup: true
          })
        }
      }
      if (row.visits4Show) {
        for (let i = 0; i < row.visits4Show.length; i++) {
          switch (row.visits4Show[i].visited) {
            case 0:
              icon = {
                type: 'map-marker',
                size: [27, 36],
                anchor: [18, 39],
                color: '#' + this.routeColors[this.mapDataIndex],
                text: (row.visits4Show[i].type === 'pickup' ? '↑' : '↓') + (i + 1)
              }
              break
            case 1:
              icon = {
                type: 'map-marker',
                size: [27, 36],
                anchor: [18, 39],
                color: '#28a745',
                text: (row.visits4Show[i].type === 'pickup' ? '↑' : '↓') + (i + 1)
              }
              break
          }

          if (row.visits4Show[i].lat !== null) {
            lat = row.visits4Show[i].lat
            lng = row.visits4Show[i].lng
          }

          markers.push({
            id: this.lastId,
            lat,
            lng,
            icon,
            x: 11,
            y: 34,
            infoText: {
              id: row.visits4Show[i].shipping_id,
              type: this.popupType,
              multi: row.visits4Show[i].shipping_ids.length > 1,
              visited: row.visits4Show[i].visited, // shippingFromVisit.status === 'PICKEDUP' || shippingFromVisit.status === 'INTRANSIT' || shippingFromVisit.status === 'DELIVERED' || shippingFromVisit.status === 'CANCELED' || shippingFromVisit.status === 'RETURNED',
              // html: infoWindowHtml,
              data: row.visits4Show[i],
              ids: row.visits4Show[i].shipping_ids,
              status: row.status
            },
            popup: true
          })
          this.lastId++
        }

        data = {
          line: {
            polyLine: row.visits4Show,
            polyLineOptions: {
              strokeColor: '#' + this.routeColors[this.mapDataIndex],
              strokeWeight: 1
            }
          },
          markers
        }

        if (this.mapDataIndex < this.routeColors.length - 1) {
          this.mapDataIndex++
        } else {
          this.mapDataIndex = 0
        }
      }
      return data
    },
    openTourMap(index) {
      let data
      this.lastId = index + 1 || 1
      this.mapDataIndex = 0
      this.markers = []
      this.polylines = []
      this.routeColors = []
      for (const tour of this.tours) {
        this.setTourColors(tour)
      }
      if (typeof index === 'number') {
        data = this.prepareTourMapData(this.items[index], index, this.withCourier)
        this.markers = data.markers
        this.polylines = [data.line]
      } else {
        const polylines = []
        const markers = []
        for (let i = 0; i < this.tours.length; i++) {
          data = this.prepareTourMapData(this.tours[i], i, this.withCourier) // this.items[i]
          if (data.markers && data.markers.length > 0) {
            polylines.push(data.line)
            for (const marker of data.markers) {
              markers.push(marker)
            }
          }
        }
        this.markers = markers
        this.polylines = polylines
      }
    },
    resized(e) {
      if (e) {
        this.$cookies.set('tour-map-height', e.height)
      }
      this.$refs.fullMap.emitReady()
    },
    preDeleteFromTour(id) {
      const tour = this.tours.find(x => x.shippings.find(y => y.id === id))
      this.$emit('delete-from-tour', tour, tour.shippings.find(x => x.id === id))
    },
    markerOverOut(e, size, anchor) {
      if (e && e.latlng) {
        let shippingIds = []
        this.tours.forEach(tour => {
          const preShippingIds = tour.shippings.filter(x => (Number.parseFloat(x.address_to.lat) === e.latlng.lat && Number.parseFloat(x.address_to.lng) === e.latlng.lng) || (Number.parseFloat(x.address_from.lat) === e.latlng.lat && Number.parseFloat(x.address_from.lng) === e.latlng.lng)).map(x => x.id)
          shippingIds = shippingIds.concat(preShippingIds)
        })

        let indexes = []
        shippingIds.forEach((shippingId, i) => {
          const subindexes = this.markers.reduce((acc, el, i) => (el.infoText.ids.includes(shippingId) ? [...acc, i] : acc), [])
          indexes = indexes.concat(subindexes)
        })

        const markers = [...new Set(this.markers)]
        for (const index of indexes) {
          markers[index].icon.size = size
          markers[index].icon.anchor = anchor
        }
        this.markers = markers
      }
    },
    setTourColors(row) {
      if (row.visits && row.visits.length > 0) {
        this.routeColors.push(this.stringToColour(row.id + row.label + (row.visits.length * 12343) + (row.visits.map(x => x.id).join('_'))).replaceAll('#', ''))
      }
    }
  }
}
</script>

<style lang="scss">
.resizable-component {
  z-index: 1;
}
.resizable-b {
  z-index: 1001 !important;
  cursor: ns-resize !important;
  bottom: -6px !important;
  height: 6px !important;
  background-color: #fff;
  border-top: 1px solid #ccc;
  &:after {
    content: '';
    display: block;
    width: 30px;
    margin: 2px auto;
    height: 2px;
    background: #999;
    border-radius: 2px;
  }
  &:hover {
    background: #ffe !important;
    border-color: #999;
    &:after {
      background: #666;
    }
  }
}
</style>
